import * as React from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export type PasswordInputProps = Omit<React.ComponentProps<"input">, "type"> & {
  /** Overrides the default «Показать пароль» aria-label on the reveal toggle. */
  showLabel?: string;
  /** Overrides the default «Скрыть пароль» aria-label on the reveal toggle. */
  hideLabel?: string;
};

/**
 * Password field with a reveal toggle — the "input trailing-adornment" pattern.
 *
 * The `<Input>` sits in a `relative` box with right padding (`pr-9`), and a
 * ghost icon-button pinned to the right toggles the input `type` between
 * "password" and "text". The toggle is `type="button"`, so it never submits
 * the surrounding form.
 *
 * `ref` and all other input props flow through to `<Input>` (React 19
 * ref-as-prop), so RHF `{...register(...)}` works unchanged.
 */
function PasswordInput({
  className,
  showLabel,
  hideLabel,
  id,
  ...props
}: PasswordInputProps): React.ReactElement {
  const { t } = useTranslation();
  const [revealed, setRevealed] = React.useState(false);
  const toggleLabel = revealed
    ? (hideLabel ?? t("common.passwordInput.hide"))
    : (showLabel ?? t("common.passwordInput.show"));

  return (
    <div className="relative">
      <Input
        id={id}
        type={revealed ? "text" : "password"}
        className={cn("pr-9", className)}
        {...props}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="text-muted-foreground hover:text-foreground absolute top-1/2 right-0.5 -translate-y-1/2"
        aria-pressed={revealed}
        aria-label={toggleLabel}
        data-testid={id ? `toggle-visibility-${id}` : "toggle-visibility"}
        onClick={() => setRevealed((v) => !v)}
      >
        {revealed ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
      </Button>
    </div>
  );
}

export { PasswordInput };
